// @owners { team: patient-health }
import { cancelAllLocalNotifications, createAllNotifications } from '~shared/features/med-reminders/notifications';
import { get, post, put } from '~shared/helpers/apiHelper';
import { type ReduxDispatch, type ReduxStateShared, type ReminderSequence } from '~shared/types';
import { type APIError } from '~shared/types/APIError';
import { type CreateReminder, type UpdateReminder } from '~shared/types/medReminders';

export const SELECT_DATE = 'SELECT_DATE';
export type ActionSelectDate = {
  type: typeof SELECT_DATE;
  payload: Date;
};
export function selectDate(date: Date): ActionSelectDate {
  return {
    type: SELECT_DATE,
    payload: date,
  };
}

export const SET_TIMEZONE_OFFSET = 'SET_TIMEZONE_OFFSET';
export type ActionSetTimezoneOffset = {
  type: typeof SET_TIMEZONE_OFFSET;
  payload: number;
};
export function setTimezoneOffset(): ActionSetTimezoneOffset {
  return {
    type: SET_TIMEZONE_OFFSET,
    payload: new Date().getTimezoneOffset() / 60,
  };
}

export const DELETE_REMINDER_LOADING = 'DELETE_REMINDER_LOADING';
export type ActionDeletingReminder = {
  type: typeof DELETE_REMINDER_LOADING;
};
export function deletingReminder(): ActionDeletingReminder {
  return {
    type: DELETE_REMINDER_LOADING,
  };
}

export const DELETE_REMINDER_SUCCEEDED = 'DELETE_REMINDER_SUCCEEDED';
export type ActionDeleteReminderSucceeded = {
  type: typeof DELETE_REMINDER_SUCCEEDED;
  payload: ReminderSequence[];
};
export function deleteReminderSucceeded(reminder: ReminderSequence[]): ActionDeleteReminderSucceeded {
  return {
    type: DELETE_REMINDER_SUCCEEDED,
    payload: reminder,
  };
}

export const DELETE_REMINDER_FAILED = 'DELETE_REMINDER_FAILED';
export type ActionDeleteReminderFailed = {
  type: typeof DELETE_REMINDER_FAILED;
  payload: APIError;
};
export function deleteReminderFailed(error: APIError): ActionDeleteReminderFailed {
  return {
    type: DELETE_REMINDER_FAILED,
    payload: error,
  };
}

export const FETCH_REMINDERS_LOADING = 'FETCH_REMINDERS_LOADING';
export type ActionFetchingReminders = {
  type: typeof FETCH_REMINDERS_LOADING;
};
export function fetchingReminders(): ActionFetchingReminders {
  return {
    type: FETCH_REMINDERS_LOADING,
  };
}

export const FETCH_REMINDERS_SUCCEEDED = 'FETCH_REMINDERS_SUCCEEDED';
export type ActionFetchRemindersSucceeded = {
  type: typeof FETCH_REMINDERS_SUCCEEDED;
  payload: ReminderSequence[];
};
export function fetchRemindersSucceeded(reminders: ReminderSequence[]): ActionFetchRemindersSucceeded {
  return {
    type: FETCH_REMINDERS_SUCCEEDED,
    payload: reminders,
  };
}

export const FETCH_REMINDERS_FAILED = 'FETCH_REMINDERS_FAILED';
export type ActionFetchRemindersFailed = {
  type: typeof FETCH_REMINDERS_FAILED;
  payload: APIError;
  error: boolean;
};
export function fetchRemindersFailed(error: APIError): ActionFetchRemindersFailed {
  return {
    type: FETCH_REMINDERS_FAILED,
    payload: error,
    error: true,
  };
}

export const CREATE_REMINDER_LOADING = 'CREATE_REMINDER_LOADING';
export type ActionCreatingReminder = {
  type: typeof CREATE_REMINDER_LOADING;
};
export function creatingReminder(): ActionCreatingReminder {
  return {
    type: CREATE_REMINDER_LOADING,
  };
}

export const CREATE_REMINDER_SUCCEEDED = 'CREATE_REMINDER_SUCCEEDED';
export type ActionCreateReminderSucceeded = {
  type: typeof CREATE_REMINDER_SUCCEEDED;
  payload: ReminderSequence;
};
export function createReminderSucceeded(reminder: ReminderSequence): ActionCreateReminderSucceeded {
  return {
    type: CREATE_REMINDER_SUCCEEDED,
    payload: reminder,
  };
}

export const CREATE_REMINDER_FAILED = 'CREATE_REMINDER_FAILED';
export type ActionCreateReminderFailed = {
  type: typeof CREATE_REMINDER_FAILED;
  payload: APIError;
  error: boolean;
};
export function createReminderFailed(error: APIError): ActionCreateReminderFailed {
  return {
    type: CREATE_REMINDER_FAILED,
    payload: error,
    error: true,
  };
}

export const UPDATE_REMINDER_LOADING = 'UPDATE_REMINDER_LOADING';
export type ActionUpdatingReminder = {
  type: typeof UPDATE_REMINDER_LOADING;
};
export function updatingReminder(): ActionUpdatingReminder {
  return {
    type: UPDATE_REMINDER_LOADING,
  };
}

export const UPDATE_REMINDER_SUCCEEDED = 'UPDATE_REMINDER_SUCCEEDED';
export type ActionUpdateReminderSucceeded = {
  type: typeof UPDATE_REMINDER_SUCCEEDED;
  payload: ReminderSequence;
};
export function updateReminderSucceeded(reminder: ReminderSequence): ActionUpdateReminderSucceeded {
  return {
    type: UPDATE_REMINDER_SUCCEEDED,
    payload: reminder,
  };
}

export const UPDATE_REMINDER_FAILED = 'UPDATE_REMINDER_FAILED';
export type ActionUpdateReminderFailed = {
  type: typeof UPDATE_REMINDER_FAILED;
  payload: APIError;
  error: boolean;
};
export function updateReminderFailed(error: APIError): ActionUpdateReminderFailed {
  return {
    type: UPDATE_REMINDER_FAILED,
    payload: error,
    error: true,
  };
}

/**
 * @deprecated Redux networking action.
 * @see https://www.notion.so/alto/Guidebook-Migrating-Redux-Networking-Actions-To-React-Query-8567e05fc96c46fcb8020595f24b0edc
 */
export function fetchReminders() {
  return (dispatch: ReduxDispatch) => {
    dispatch(fetchingReminders());
    return get('/reminders').then((json) => {
      if (json.error) {
        dispatch(fetchRemindersFailed(json.error));
        return false;
      }

      dispatch(fetchRemindersSucceeded(json));
      dispatch(resetNotifications());
      return true;
    });
  };
}

/**
 * @deprecated Redux networking action.
 * @see https://www.notion.so/alto/Guidebook-Migrating-Redux-Networking-Actions-To-React-Query-8567e05fc96c46fcb8020595f24b0edc
 */
export function createReminder(reminder: CreateReminder) {
  return (dispatch: ReduxDispatch) => {
    dispatch(creatingReminder());
    return post('/reminders', reminder).then((json) => {
      if (json.error) {
        dispatch(createReminderFailed(json));
        return false;
      }

      dispatch(createReminderSucceeded(json));
      dispatch(resetNotifications());
      return true;
    });
  };
}

/**
 * @deprecated Redux networking action.
 * @see https://www.notion.so/alto/Guidebook-Migrating-Redux-Networking-Actions-To-React-Query-8567e05fc96c46fcb8020595f24b0edc
 */
export function updateReminder(reminder: UpdateReminder) {
  return (dispatch: ReduxDispatch) => {
    dispatch(updatingReminder());
    return put(`/reminders/${reminder.reminder_sequence_id}`, reminder).then((json) => {
      if (json.error) {
        dispatch(updateReminderFailed(json));
        return false;
      }

      dispatch(updateReminderSucceeded(json));
      dispatch(resetNotifications());
      return true;
    });
  };
}

/**
 * @deprecated Redux networking action.
 * @see https://www.notion.so/alto/Guidebook-Migrating-Redux-Networking-Actions-To-React-Query-8567e05fc96c46fcb8020595f24b0edc
 */
export function deleteReminder(reminderSequenceIDs: number[]) {
  return (dispatch: ReduxDispatch) => {
    dispatch(deletingReminder());
    return post('/reminders/destroy', {
      ids: reminderSequenceIDs,
    }).then((json) => {
      if (json.error) {
        dispatch(deleteReminderFailed(json));
        return false;
      }

      dispatch(deleteReminderSucceeded(json));
      dispatch(resetNotifications());
      return true;
    });
  };
}

export function resetNotifications() {
  return (_: ReduxDispatch, getState: () => ReduxStateShared) => {
    const state = getState();
    cancelAllLocalNotifications();
    createAllNotifications(state);
  };
}

export type ReminderAction =
  | ActionCreateReminderSucceeded
  | ActionCreateReminderFailed
  | ActionCreatingReminder
  | ActionDeleteReminderSucceeded
  | ActionDeleteReminderFailed
  | ActionDeletingReminder
  | ActionFetchRemindersSucceeded
  | ActionFetchRemindersFailed
  | ActionFetchingReminders
  | ActionUpdateReminderSucceeded
  | ActionUpdateReminderFailed
  | ActionUpdatingReminder;
